import { Project, Stage, Subtask, Task, User } from 'features/projects/projectsTypes';
import { AVAILABLE_ROLES } from 'components/projectCreate/utils';
import i18n from 'i18next';

// Type for user with auth context (from Redux auth state)
interface AuthUser {
  user: User & { superAdmin?: boolean };
}

// Legacy budget type for calculateTotalBudget (old budget structure)
interface LegacyBudget {
  materialCosts?: number;
  officeOverhead?: number;
  companyProfit?: number;
  permitCosts?: number;
  draftsmanPay?: number;
  installerPay?: number;
  salesmenPay?: number;
  salesManagerPay?: number;
  projectManagerPay?: number;
}

const getStageStatusColor = (stage: Stage, isCurrentStage: boolean) => {
  if (stage.completed) {
    return 'bg-green-600 border-green-600 text-white';
  }
  if (isCurrentStage) {
    return 'bg-blue-600 border-blue-600 text-white';
  }
  return 'bg-white dark:bg-zinc-500 border-gray-300 border-gray-100 text-gray-500';
};

const formatStageName = (stage: string) => {
  if (!stage) return '';
  return stage
    .split('_')
    .map(word => word.charAt(0) + word.toLowerCase().slice(1))
    .join(' ');
};

const getStageStatusLabel = (stage: Stage, isCurrentStage: boolean) =>
  stage.completed ? 'Completed' : isCurrentStage ? 'In Progress' : 'Pending';

const getTaskStatus = (task: Task) => {
  if (task.completed) return "completed";

  if (!task.subtasks || task.subtasks.length === 0) return "pending";

  const allSubtasksFalse = task.subtasks.every((subtask) => !subtask.completed);
  return allSubtasksFalse ? "pending" : "in progress";
};

/** @deprecated Duration is now managed at stage level */
const calculateTaskDuration = (_task: Task): number => {
  return 0;
};

const calculateStageDuration = (stage: Stage): number => {
  // Use stage-level duration if available, otherwise return 0
  return stage.duration || 0;
};

const calculateTotalProjectDuration = (stages: Stage[]) => {
  if (!stages || !Array.isArray(stages)) return 0;
  return stages.reduce((total, stage) => total + calculateStageDuration(stage), 0);
};

/** @deprecated Legacy budget calculation - new budgets use itemGroups */
const calculateTotalBudget = (budget: LegacyBudget | null): number => {
  if (!budget) return 0;

  const budgetFields: (keyof LegacyBudget)[] = [
    'materialCosts',
    'officeOverhead',
    'companyProfit',
    'permitCosts',
    'draftsmanPay',
    'installerPay',
    'salesmenPay',
    'salesManagerPay',
    'projectManagerPay'
  ];

  return budgetFields.reduce((total, field) => {
    const value = parseFloat(String(budget[field] ?? 0)) || 0;
    return total + value;
  }, 0);
};

const getAssignableSubtasksCount = (user: Pick<User, '_id'> | null, stage: Stage): number => {
  if (!stage?.tasks) return 0;

  return stage.tasks.reduce((count, task) => {
    return count + (task.subtasks?.filter(subtask => {
      const isNotAssigned = !subtask.assignees?.some(a => a._id === user?._id);
      const isNotCompleted = !subtask.completed;
      return isNotAssigned && isNotCompleted;
    }).length || 0);
  }, 0);
};

const canCompleteSubtask = (authUser: AuthUser | null, subtask: Subtask): boolean => {
  if (!subtask || !authUser?.user) {
    return false;
  }

  // SuperAdmin can complete any subtask
  if (authUser.user.superAdmin === true) return true;

  // Check if user is assigned to this subtask
  const isAssigned = subtask.assignees?.some(assignee =>
    assignee._id === authUser.user._id
  );

  return isAssigned ?? false;
};

type BadgeColor = 'purple' | 'indigo' | 'cyan' | 'emerald' | 'amber' | 'blue' | 'zinc';

const getRoleTagColor = (role: string | null | undefined): BadgeColor => {
  if (!role) return 'zinc';

  // For assigned users, use vibrant colors based on role
  if (role.startsWith('assigned-')) {
    const roleKey = role.replace('assigned-', '').toLowerCase();
    const colors: Record<string, BadgeColor> = {
      admin: 'purple',
      manager: 'indigo',
      draftsman: 'cyan',
      installer: 'emerald',
      salesman: 'amber',
    };
    return colors[roleKey] || 'blue';
  }

  // For role tags (showing which roles can be assigned), use gray
  return 'zinc';
};

const formatUserName = (user: Partial<User> | null): string => {
  if (!user) return i18n.t('CREATE_PROJECT_UNASSIGNED', { ns: 'projects' });

  if (user.firstName && user.lastName) {
    return `${user.firstName} ${user.lastName}`;
  }

  return user.email || i18n.t('CREATE_PROJECT_UNASSIGNED', { ns: 'projects' });
};

const getRoleLabel = (value: string): string => {
  const role = AVAILABLE_ROLES.find(role => role.value === value);
  return role?.label || value;
};

interface CostItemWithCategory {
  category: string;
  [key: string]: unknown;
}

const groupCostItemsByCategory = <T extends CostItemWithCategory>(
  costItems: T[]
): Record<string, T[]> =>
  costItems.reduce<Record<string, T[]>>((acc, item) => {
    if (!acc[item.category]) {
      acc[item.category] = [];
    }
    acc[item.category].push(item);
    return acc;
  }, {});

interface ItemWithOrder {
  order: number;
  [key: string]: unknown;
}

const mapTaskArrayToObject = <T extends ItemWithOrder>(
  arr: T[]
): Record<number, T> =>
  arr.reduce<Record<number, T>>((acc, item) => ({ ...acc, [item.order]: item }), {});

const getProjectUsers = (currentProject: Project) => {
  const userMap = new Map();

  currentProject.stages.forEach(stage => {
    stage.tasks.forEach(task => {
      task.subtasks.forEach(subtask => {
        subtask.assignees?.forEach(assignee => {
          if (!userMap.has(assignee._id)) {
            userMap.set(assignee._id, {
              ...assignee,
              assignments: []
            });
          }
          
          userMap.get(assignee._id).assignments.push({
            stageName: stage.name,
            taskName: task.name,
            subtaskName: subtask.name,
            completed: subtask.completed,
            duration: 0 // Duration is now managed at stage level
          });
        });
      });
    });
  });

  return Array.from(userMap.values());
};

const getCurrentStageIndex = (stages: Stage[]) =>
  stages.findIndex(stage => stage.isCurrentStage);

const getProjectTaskStats = (project: Project): { total: number; completed: number } => {
  const allTasks = project.stages.flatMap(stage => stage.tasks);
  const total = allTasks.length;
  const completed = allTasks.filter(task => task.completed).length;

  return { total, completed };
};

// Helper to validate MongoDB ObjectId format (24-char hex string)
// Used to filter out legacy enum values like "TECHNICIAN", "ADMIN", etc.
const isValidMongoId = (id: unknown): boolean => {
  if (typeof id !== 'string') return false;
  return /^[a-fA-F0-9]{24}$/.test(id);
};

const sanitizeStagesData = (stages: Stage[]) => {

  if (!stages || !Array.isArray(stages)) {
    return stages;
  }

  return stages.map((stage: any) => {
    const newStage = { ...stage };

    if (newStage._id?.startsWith('new') || newStage._id === '-1') {
      delete newStage._id;
    }

    // Remove fields not in backend DTO
    delete newStage.startDate;
    delete newStage.endDate;

    // Convert empty-string status to undefined (form sends "" for "None")
    if (!newStage.status) {
      delete newStage.status;
    }

    newStage.tasks = (newStage.tasks || []).map((task: any) => {
      const newTask = { ...task };

      if (newTask._id?.startsWith('new') || newTask._id === '-1') {
        delete newTask._id;
      }

      newTask.subtasks = (newTask.subtasks || []).map((subtask: any) => {
        const newSubtask = { ...subtask };

        if (newSubtask._id?.startsWith('new') || newSubtask._id === '-1') {
          delete newSubtask._id;
        }

        // Filter out legacy enum role values (TECHNICIAN, ADMIN, etc.)
        // Backend now expects MongoDB ObjectIds only
        if (Array.isArray(newSubtask.roles)) {
          newSubtask.roles = newSubtask.roles.filter(isValidMongoId);
        }

        // Remove fields not in backend DTO
        delete newSubtask.estimatedBudget;
        delete newSubtask.assignedOn;
        delete newSubtask.requiredPermissions;

        return newSubtask;
      });

      return newTask;
    });

    return newStage;
  });
}

export {
  getStageStatusColor,
  formatStageName,
  formatUserName,
  getStageStatusLabel,
  getTaskStatus,
  getAssignableSubtasksCount,
  getRoleTagColor,
  getRoleLabel,
  calculateTaskDuration,
  calculateStageDuration,
  calculateTotalProjectDuration,
  calculateTotalBudget,
  canCompleteSubtask,
  groupCostItemsByCategory,
  mapTaskArrayToObject,
  getProjectUsers,
  getCurrentStageIndex,
  getProjectTaskStats,
  sanitizeStagesData,
};
